import Vue from "vue";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "@/features/tour/driverTheme.css";
import { getPreference, savePreference } from "@/services/userPreferencesService";

/**
 * Motor de tour guiado — renderização pelo driver.js (1.x), embrulhado numa
 * camada nossa que cuida do que é específico do produto:
 *
 * - "Primeira visita" persiste por USUÁRIO (tabela user_preferences, chave única
 *   `tours_seen` → mapa { tourId: true }), com fallback em localStorage.
 * - Registro do tour da página atual, para o botão flutuante "?" (replay).
 * - Passos declarados por página no formato { anchor, title, description }.
 *
 * `anchor` casa com data-tour="..." no elemento alvo. Passo sem `anchor` vira
 * um card centralizado (ex.: boas-vindas). O driver.js cuida de spotlight,
 * posicionamento, scroll, resize, teclado e acessibilidade.
 */

const PREF_KEY = "tours_seen";

const state = Vue.observable({
  active: false,
  tourId: null,
  // Tour da página atualmente montada (alimenta o botão flutuante de replay).
  registered: null, // { tourId } | null
});

// Cache do mapa de tours já vistos (carregado uma vez por sessão).
let seenCache = null;
// Pilha de tours registrados ({ tourId, getSteps }). O topo alimenta o "?" —
// pilha (e não slot único) para páginas com sub-views (ex.: Agenda + Kanban),
// onde a sub-view registra por cima e, ao sair, restaura o tour da página.
let registrations = [];
// Instância viva do driver.js (uma por vez).
let activeDriver = null;

// Espelha o topo da pilha no estado reativo (para o botão flutuante).
function syncRegistered() {
  const top = registrations[registrations.length - 1] || null;
  state.registered = top ? { tourId: top.tourId } : null;
}

function readLocal() {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function writeLocal(map) {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(map));
  } catch {
    /* storage indisponível — ignora */
  }
}

async function loadSeen() {
  if (seenCache) return seenCache;
  const local = readLocal();
  let remote = null;
  try {
    remote = await getPreference(PREF_KEY);
  } catch {
    remote = null;
  }
  // Remoto é a fonte de verdade; local só complementa (offline/primeiro save).
  seenCache = { ...local, ...(remote && typeof remote === "object" ? remote : {}) };
  return seenCache;
}

async function markSeen(tourId) {
  const seen = await loadSeen();
  if (seen[tourId]) return;
  seen[tourId] = true;
  seenCache = { ...seen };
  writeLocal(seenCache);
  try {
    await savePreference(PREF_KEY, seenCache);
  } catch {
    /* já gravou local; sincroniza na próxima vez */
  }
}

/**
 * Converte nossos passos para o formato do driver.js, descartando os que
 * apontam para um elemento inexistente (evita passo "vazio").
 */
function toDriverSteps(steps) {
  return (steps || [])
    .filter((s) => !s.anchor || document.querySelector(`[data-tour="${s.anchor}"]`))
    .map((s) => ({
      element: s.anchor ? `[data-tour="${s.anchor}"]` : undefined,
      popover: {
        title: s.title || "",
        description: s.description || "",
        side: s.side, // opcional (driver escolhe se undefined)
        align: s.align,
      },
    }));
}

const controller = {
  state,

  /** A página monta: registra seu tour para o botão flutuante de replay. */
  register(tourId, getSteps) {
    registrations = registrations.filter((r) => r.tourId !== tourId);
    registrations.push({
      tourId,
      getSteps: typeof getSteps === "function" ? getSteps : null,
    });
    syncRegistered();
  },

  /** A página desmonta: remove o registro dela (o topo anterior volta a valer). */
  unregister(tourId) {
    registrations = registrations.filter((r) => r.tourId !== tourId);
    syncRegistered();
  },

  /** Replay do tour do topo da pilha (botão flutuante "?"). */
  replayCurrent() {
    const top = registrations[registrations.length - 1];
    if (top && top.getSteps) this.start(top.tourId, top.getSteps());
  },

  /** Já viu este tour? (para decidir auto-start). */
  async hasSeen(tourId) {
    const seen = await loadSeen();
    return !!seen[tourId];
  },

  /** Inicia o tour imediatamente (usado pelo botão "?" / replay). */
  start(tourId, steps) {
    const driverSteps = toDriverSteps(steps);
    if (!driverSteps.length) return false;

    if (activeDriver) activeDriver.destroy();

    activeDriver = driver({
      showProgress: true,
      progressText: "{{current}} de {{total}}",
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayColor: "#0f1e19",
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 12,
      popoverClass: "salvia-tour",
      nextBtnText: "Próximo",
      prevBtnText: "Voltar",
      doneBtnText: "Concluir",
      showButtons: ["next", "previous", "close"],
      steps: driverSteps,
      onDestroyed: () => {
        state.active = false;
        state.tourId = null;
        activeDriver = null;
      },
    });

    state.tourId = tourId;
    state.active = true;
    activeDriver.drive();
    return true;
  },

  /** Auto-start só na primeira visita do usuário àquela página. */
  async maybeAutoStart(tourId, steps) {
    if (await this.hasSeen(tourId)) return false;
    const started = this.start(tourId, steps);
    if (started) markSeen(tourId); // marca ao exibir (não reaparece)
    return started;
  },

  /** Fecha o tour ativo (ex.: ao trocar de página). */
  stop() {
    if (activeDriver) activeDriver.destroy(); // dispara onDestroyed → limpa estado
  },
};

export default controller;
